import type { CollectionDto } from "@serophin/nottldr-shared";
import { useCollections } from "@serophin/nottldr-shared/hooks";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";
import { Modal } from "../Modal";
import { CollectionSelect, ModalButtons } from "./CollectionSelect";
import { articleCount, errorMessage } from "./collectionText";

export type CollectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  collection: CollectionDto;
  allCollections: CollectionDto[];
};

const useCopy = (collection: CollectionDto, onClose: () => void) => {
  const toast = useToast();
  const { copy } = useCollections();
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!target) {
      toast.error("Please select a target collection");
      return;
    }
    setBusy(true);
    try {
      const copiedAll = await copy(collection.id, target);
      if (copiedAll) {
        toast.success("Articles copied successfully!");
      } else {
        toast.error("Not all articles were copied. Please select a larger target collection.");
      }
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to copy articles"));
    } finally {
      setBusy(false);
    }
  };

  return { target, setTarget, busy, run };
};

const Choice = (props: { collection: CollectionDto; others: CollectionDto[]; target: string; busy: boolean; onChange: (id: string) => void }) => {
  if (props.collection.articleCount === 0) {
    return <p class="text-sm text-yellow-600 dark:text-yellow-500">This collection has no articles to copy.</p>;
  }
  if (props.others.length === 0) {
    return <p class="text-sm text-yellow-600 dark:text-yellow-500">No other collections available. Create a collection first.</p>;
  }
  return (
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select target collection</label>
      <CollectionSelect value={props.target} options={props.others} placeholder="Select a collection" disabled={props.busy} onChange={props.onChange} />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{articleCount(props.collection.articleCount)} will be copied</p>
    </div>
  );
};

export const CopyCollectionModal = ({ isOpen, onClose, collection, allCollections }: CollectionModalProps) => {
  const { target, setTarget, busy, run } = useCopy(collection, onClose);
  const close = () => {
    setTarget("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <div class="py-2">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Copy Collection</h3>
        <div class="space-y-4">
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Copy all articles from <strong>{collection.name}</strong> to another collection.
          </p>
          <Choice collection={collection} others={allCollections.filter((c) => c.id !== collection.id)} target={target} busy={busy} onChange={setTarget} />
          <ModalButtons onCancel={close} onConfirm={run} busy={busy} disabled={!target || collection.articleCount === 0} label="Copy" busyLabel="Copying..." />
        </div>
      </div>
    </Modal>
  );
};
